import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const AboutPage = () => {
  useEffect(() => {
    // Update page title and meta
    document.title = '關於我們 - Flan Cake 芙蘭蛋糕的百年傳承故事';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Flan Cake 芙蘭蛋糕起源於16世紀西班牙，傳入拉丁美洲後融合當地食材演變成經典甜點。我們致力於將這份傳統美味帶到台灣，讓更多人體驗南美的甜蜜文化。50年傳承經驗，10000+滿意顧客。');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "關於 Flan Cake 芙蘭蛋糕",
      "description": "Flan 起源於16世紀的西班牙，隨著殖民時期傳入拉丁美洲後，融合當地食材與烹飪技法，演變成今日我們熟知的 Flan Cake。",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Flan Cake 芙蘭蛋糕",
        "foundingDate": "1970",
        "description": "致力於將南美傳統美味帶到台灣的甜點品牌"
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Flan Cake 芙蘭蛋糕，源自南美洲的經典甜點，絲滑細膩的口感與濃郁焦糖香氣。提供線上購物、門市洽詢與加盟服務，將百年傳承的南美甜蜜帶到台灣。芙蘭蛋糕專賣店提供經典原味、咖啡、抹茶等多種口味，台北、台中、高雄、台南門市籌備中。');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-lavender-50">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div className="space-y-8">
                <div className="inline-block px-4 py-2 border border-lavender-400 rounded-full">
                  <span className="text-sm text-lavender-700">我們的故事</span>
                </div>

                <div>
                  <h1 className="font-serif text-5xl md:text-6xl font-bold text-gray-800 mb-2">
                    Flan Cake 的
                  </h1>
                  <h2 className="font-serif text-4xl md:text-5xl font-light text-lavender-700">
                    百年傳承 🎂
                  </h2>
                </div>

                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    Flan 起源於 16 世紀的西班牙，隨著殖民時期傳入拉丁美洲後，融合當地食材與烹飪技法，演變成今日我們熟知的 Flan Cake。這款甜點以其絲滑細膩的口感和濃郁的焦糖香氣，成為南美家庭聚會中不可或缺的經典甜點。
                  </p>
                  <p>
                    我們致力於將這份傳統美味帶到台灣，讓更多人體驗南美的甜蜜文化。每一個 Flan Cake 都是由經驗豐富的甜點師傅手工製作，嚴選最優質的食材，遵循傳統配方，同時加入創新元素，為您呈現最完美的味覺享受。
                  </p>
                </div>

                <a href="#history" className="inline-block text-lavender-700 font-medium border-b-2 border-lavender-600 pb-1 hover:text-lavender-600 transition-colors cursor-pointer">
                  了解更多歷史
                </a>
              </div>

              <div className="relative">
                <div className="absolute top-0 right-0 flex items-center gap-12">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-800">50+</div>
                    <div className="text-sm text-gray-600 mt-2">年傳承經驗</div>
                  </div>
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-800">10000+</div>
                    <div className="text-sm text-gray-600 mt-2">滿意顧客</div>
                  </div>
                </div>

                <div className="mt-32 space-y-6">
                  <div className="relative w-64 h-64 rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://public.readdy.ai/ai/img_res/ba74b0fa6e69bb30255b4f218bc03189.jpg"
                      alt="傳統製作"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="relative w-80 h-96 rounded-2xl overflow-hidden shadow-xl ml-auto">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20elegant%20flan%20cake%20presentation%20on%20a%20pristine%20white%20marble%20pedestal%2C%20professional%20studio%20lighting%20with%20soft%20shadows%2C%20contemporary%20minimalist%20style%2C%20the%20flan%20has%20perfect%20glossy%20caramel%20glaze%20dripping%20down%20sides%2C%20shot%20from%20slightly%20elevated%20angle%2C%20clean%20white%20background%2C%20high-end%20culinary%20photography&width=600&height=800&seq=about-modern&orientation=portrait"
                      alt="現代精緻"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="history" className="mt-32 px-6 bg-lavender-100 py-24">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-4xl font-serif font-bold text-center text-gray-800 mb-16">
              Flan Cake 的歷史演變
            </h3>

            <div className="space-y-12">
              <div className="flex gap-8">
                <div className="flex-shrink-0 w-32 text-right">
                  <div className="text-3xl font-serif font-bold text-lavender-700">16世紀</div>
                </div>
                <div className="flex-1 border-l-2 border-lavender-400 pl-8 pb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">西班牙起源</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Flan 最早起源於古羅馬時期，但在 16 世紀的西班牙發展成熟。當時的西班牙人將雞蛋、牛奶和糖混合，創造出這款經典甜點。
                  </p>
                </div>
              </div>

              <div className="flex gap-8">
                <div className="flex-shrink-0 w-32 text-right">
                  <div className="text-3xl font-serif font-bold text-lavender-700">17世紀</div>
                </div>
                <div className="flex-1 border-l-2 border-lavender-400 pl-8 pb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">傳入拉丁美洲</h4>
                  <p className="text-gray-700 leading-relaxed">
                    隨著西班牙殖民者來到新大陸，Flan 也傳入了拉丁美洲。當地人加入了椰奶、咖啡等本土食材，創造出獨特的風味變化。
                  </p>
                </div>
              </div>

              <div className="flex gap-8">
                <div className="flex-shrink-0 w-32 text-right">
                  <div className="text-3xl font-serif font-bold text-lavender-700">19世紀</div>
                </div>
                <div className="flex-1 border-l-2 border-lavender-400 pl-8 pb-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">成為經典</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Flan 逐漸成為拉丁美洲家庭聚會和節慶中不可或缺的甜點，每個國家都發展出自己的特色版本。
                  </p>
                </div>
              </div>

              <div className="flex gap-8">
                <div className="flex-shrink-0 w-32 text-right">
                  <div className="text-3xl font-serif font-bold text-lavender-700">21世紀</div>
                </div>
                <div className="flex-1 border-l-2 border-lavender-400 pl-8">
                  <h4 className="text-xl font-semibold text-gray-800 mb-3">走向世界</h4>
                  <p className="text-gray-700 leading-relaxed">
                    如今，Flan Cake 已經成為全球知名的甜點，我們將這份傳統美味帶到台灣，讓更多人能夠品嚐到正宗的南美風味。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutPage;
